// src/App.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";

import "./style.css";

import { getLangFromPathname } from "./i18n";

// Pages
import Home from "./Pages/Home/Home";
import NotFound from "./Pages/NotFound/NotFound";
import Privacy from "./Pages/Privacy/Privacy";
import WebPricingLandingPage from "./Pages/WebPricingLandingPage/WebPricingLandingPage";
import MusicianWebsiteLandingPage from "./Pages/MusicianWebsiteLandingPage/MusicianWebsiteLandingPage";
import BandWebsiteLandingPage from "./Pages/BandWebsiteLandingPage/BandWebsiteLandingPage";
import BusinessWebsiteLandingPage from "./Pages/BusinessWebsiteLandingPage/BusinessWebsiteLandingPage";
import PhotographerWebsiteLandingPage from "./Pages/PhotographerWebsiteLandingPage/PhotographerWebsiteLandingPage";
import LogoDesignLandingPage from "./Pages/LogoDesignLandingPage/LogoDesignLandingPage";

// Components
import Navbar from "./components/Navbar/navbar";
import CookieConsent from "./components/CookieConsent/CookieConsent";
import GlobalParallaxBackground from "./components/GlobalParallaxBackground/GlobalParallaxBackground";
import HashAnchorScroll from "./components/HashAnchorScroll/HashAnchorScroll";
import FloatingActionBar from "./components/FloatingActionBar/FloatingActionBar";
import { SiteContentProvider } from "./content/SiteContentContext";

/**
 * Kevyt visuaalinen flash kielenvaihdossa.
 * Ei remounttaa sivua eikä muuta scroll-positiota.
 */
const LangFlash = ({ trigger }) => {
  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion) return undefined;

    setOpacity(0.18);

    const raf = window.requestAnimationFrame(() => {
      setOpacity(0);
    });

    return () => {
      window.cancelAnimationFrame(raf);
    };
  }, [trigger]);

  return (
    <div
      aria-hidden="true"
      className="
        pointer-events-none fixed inset-0 z-[998]
        bg-white/40 backdrop-blur-[1.5px]
        transition-opacity duration-300 ease-out
        dark:bg-black/20
      "
      style={{ opacity }}
    />
  );
};

/**
 * Kielipolun käsittely:
 *
 * /                         -> fi, prefix ""
 * /en/                      -> en, prefix "/en"
 * /es/                      -> es, prefix "/es"
 * /en/verkkosivujen-hinta   -> reititykselle /verkkosivujen-hinta
 *
 * Varsinaiset React Router -reitit voidaan näin pitää samoina kaikilla kielillä.
 */
const getLanguageRouteData = (pathname) => {
  const currentPath =
    typeof pathname === "string" && pathname
      ? pathname
      : "/";

  const lang = getLangFromPathname(currentPath);
  const prefix = lang === "fi" ? "" : `/${lang}`;

  let strippedPathname = currentPath;

  if (prefix && currentPath.startsWith(prefix)) {
    strippedPathname = currentPath.slice(prefix.length);
  }

  if (!strippedPathname) {
    strippedPathname = "/";
  }

  if (!strippedPathname.startsWith("/")) {
    strippedPathname = `/${strippedPathname}`;
  }

  return {
    lang,
    prefix,
    strippedPathname,
  };
};

function App() {
  const location = useLocation();
  const { pathname } = location;

  const {
    lang,
    prefix,
    strippedPathname,
  } = useMemo(
    () => getLanguageRouteData(pathname),
    [pathname]
  );

  /**
   * <html lang=""> asetetaan App-tasolla.
   *
   * Näin oikea dokumenttikieli ei ole Navbarin tai yksittäisen komponentin
   * vastuulla ja FI / EN / ES päivittyy varmasti kaikilla reiteillä.
   */
  useEffect(() => {
    if (typeof document === "undefined") return;

    document.documentElement.lang = lang;
  }, [lang]);

  /**
   * Routes saa location-olion, josta kieliprefiksi on poistettu.
   * Hash, search ja muut location-tiedot säilyvät ennallaan.
   */
  const routedLocation = useMemo(
    () => ({
      ...location,
      pathname: strippedPathname,
    }),
    [location, strippedPathname]
  );

  return (
    <SiteContentProvider>
      <div className="App min-h-screen w-full pt-16">
        <GlobalParallaxBackground />

        <Navbar
          lang={lang}
          prefix={prefix}
        />

        <LangFlash trigger={lang} />

        <CookieConsent lang={lang} />

        <FloatingActionBar
          lang={lang}
          prefix={prefix}
        />

        {/*
          Hash-ankkuriscroll:
          /#contact
          /#works
          /en/#contact
          /es/#works
        */}
        <HashAnchorScroll />

        <div className="relative w-full">
          <Routes location={routedLocation}>
            {/* Etusivu */}
            <Route
              path="/"
              element={
                <Home
                  lang={lang}
                  prefix={prefix}
                />
              }
            />

            {/* Verkkosivujen hinta */}
            <Route
              path="/verkkosivujen-hinta"
              element={
                <WebPricingLandingPage
                  lang={lang}
                  prefix={prefix}
                />
              }
            />

            <Route
              path="/verkkosivujen-hinta/"
              element={
                <WebPricingLandingPage
                  lang={lang}
                  prefix={prefix}
                />
              }
            />

            {/* Tietosuoja */}
            <Route
              path="/tietosuoja"
              element={
                <Privacy
                  lang={lang}
                  prefix={prefix}
                />
              }
            />

            <Route
              path="/tietosuoja/"
              element={
                <Privacy
                  lang={lang}
                  prefix={prefix}
                />
              }
            />

            {/* Muusikoiden verkkosivut */}
            <Route
              path="/verkkosivut-muusikoille"
              element={
                <MusicianWebsiteLandingPage
                  lang={lang}
                  prefix={prefix}
                />
              }
            />

            <Route
              path="/verkkosivut-muusikoille/"
              element={
                <MusicianWebsiteLandingPage
                  lang={lang}
                  prefix={prefix}
                />
              }
            />

            {/* Bändien verkkosivut */}
            <Route
              path="/verkkosivut-bandeille"
              element={
                <BandWebsiteLandingPage
                  lang={lang}
                  prefix={prefix}
                />
              }
            />

            <Route
              path="/verkkosivut-bandeille/"
              element={
                <BandWebsiteLandingPage
                  lang={lang}
                  prefix={prefix}
                />
              }
            />

            {/* Yritysten verkkosivut */}
            <Route
              path="/verkkosivut-yrityksille"
              element={
                <BusinessWebsiteLandingPage
                  lang={lang}
                  prefix={prefix}
                />
              }
            />

            <Route
              path="/verkkosivut-yrityksille/"
              element={
                <BusinessWebsiteLandingPage
                  lang={lang}
                  prefix={prefix}
                />
              }
            />

            {/* Valokuvaajien verkkosivut */}
            <Route
              path="/valokuvaajan-verkkosivut"
              element={
                <PhotographerWebsiteLandingPage
                  lang={lang}
                  prefix={prefix}
                />
              }
            />

            <Route
              path="/valokuvaajan-verkkosivut/"
              element={
                <PhotographerWebsiteLandingPage
                  lang={lang}
                  prefix={prefix}
                />
              }
            />

            {/* Logon suunnittelu */}
            <Route
              path="/logon-suunnittelu"
              element={
                <LogoDesignLandingPage
                  lang={lang}
                  prefix={prefix}
                />
              }
            />

            <Route
              path="/logon-suunnittelu/"
              element={
                <LogoDesignLandingPage
                  lang={lang}
                  prefix={prefix}
                />
              }
            />

            {/* 404 */}
            <Route
              path="*"
              element={
                <NotFound
                  lang={lang}
                  prefix={prefix}
                />
              }
            />
          </Routes>
        </div>
      </div>
    </SiteContentProvider>
  );
}

export default App;